import React, { Suspense, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import WebfontLoader from '@dr-kobros/react-webfont-loader';
import { Edit, Eye, EyeOff, MessageSquare } from 'react-feather';
import { useRemote } from '../../Utils/Utils';
import { DataContext } from '../../DataController';
import { RawEditableText as RawText } from '../../contents/EditableText';
import { RawEditableText as RawFinalisedText } from '../../contents/FinalisedEditableText';
import { RawEditableText as RawStaticData } from '../../contents/SlateStaticData';
import createSnapshotData from './snapshotData';
import Tooltip from '../../components/popups/Tooltip';
import VersionSelect from './VersionSelect';
import { compareSlateAddins } from './compareTocAddin';
import { livePaneProps, resolveBlockValue, resolveSheetBackground, sheetScale, showsLiveBadge } from './paneContent';

// One compare pane (RES-561, plan decision D3): walks the template's pages[].content[]
// in reading order. Slate-backed blocks render through the Raw editor components with
// that side's ANNOTATED content when the diff is ready (diff leaf marks styled by the
// diffMarksAddin classes), falling back to the plain stored value while the other
// side loads; every other block renders its REAL component against a per-pane
// snapshot data context — those show current imported fund data and are not diffed
// (each carries a "Live data" badge).

// Names → components; the diffable-name list lives in paneContent.js.
const SLATE_RAW_COMPONENTS = {
	EditableText: RawText,
	FinalisedEditableText: RawFinalisedText,
	SlateStaticData: RawStaticData,
};

// PDF parity for stored hard line breaks (RES-561 item 1, option 1C): the PDF
// replaces \n with a space before layout (PDFFunctions.tabsToSpaces), but
// slate-react forces white-space: pre-wrap inline, so stored \n's would render
// literally and wrap paragraphs early. editableStyle rides slate-react's own
// style merge (via @threeskye/editor-component-slate >= 0.0.95); the authoring
// editor keeps pre-wrap — only compare overrides.
// Exported for the rawCompareRendering guard test (shared-contract pattern).
export const COMPARE_EDITABLE_STYLE = { whiteSpace: 'normal' };

const lazyCache = {};
function lazyContent(className) {
	if (!lazyCache[className])
		lazyCache[className] = React.lazy(() => import('../../contents/' + className));
	return lazyCache[className];
}

// One block may throw without taking the whole pane down.
class BlockErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}

	componentDidCatch(error) {
		console.log('Compare block failed to render', error);
		this.setState({ hasError: true });
	}

	render() {
		if (this.state.hasError)
			return <div className="compare-block-error">This block could not be displayed</div>;
		return this.props.children;
	}
}

const CompareBlock = ({ block, side, templateConfig, contentMap, diffKeys, additionalAddins }) => {
	const config = block.configuration;
	// On a sheet page (2A layout pages, 3A flow segments) every block carries its
	// template location and anchors absolutely inside the sheet, at the same pt
	// coordinates the editor's AbsoluteComponent and the PDF use. Flat-stacked
	// pages have location: null. A flow body also carries its measure (3A) —
	// the sheet width minus the flow margins.
	const positionClass = block.location ? ' compare-block-positioned' : '';
	const positionStyle = block.location
		? {
			position: 'absolute',
			left: block.location.left + 'pt',
			top: block.location.top + 'pt',
			width: block.width ? block.width + 'pt' : undefined,
		}
		: undefined;
	const Raw = SLATE_RAW_COMPONENTS[block.className];
	if (Raw && config && config.dataName) {
		const diffEntry = diffKeys ? diffKeys[config.dataName] : undefined;
		const resolved = resolveBlockValue(diffEntry, side, contentMap[config.dataName]);
		const { status, incomparable } = resolved;
		// A flow-page segment (item 6) renders its slice of the body's top-level
		// nodes; the annotated tree keeps the stored value's node count/order, so
		// the persisted break indices address it directly.
		const value = resolved.value && block.segment
			? resolved.value.slice(block.segment.from, block.segment.to)
			: resolved.value;
		const statusClass = status && status !== 'unchanged' ? ' compare-block-' + status : '';
		if ((value && value.length) || incomparable) {
			return (
				<div className={'compare-block compare-block-slate' + statusClass + positionClass} style={positionStyle} data-content-key={config.dataName}>
					{incomparable && (
						<div className="compare-block-notice">This content's saved format can't be compared</div>
					)}
					{value && value.length > 0 && (
						<BlockErrorBoundary>
							<Raw
								config={config}
								styles={templateConfig.styles}
								load={value}
								editable={false}
								noEditStates
								editableStyle={COMPARE_EDITABLE_STYLE}
								additionalAddins={compareSlateAddins(additionalAddins, templateConfig.styles, config)}
							/>
						</BlockErrorBoundary>
					)}
				</div>
			);
		}
		// No stored Slate value on this side (key never saved, or absent from this
		// version) — render nothing rather than a live editor with phantom content.
		return <div className={'compare-block compare-block-empty' + statusClass + positionClass} style={positionStyle} data-content-key={config.dataName} />;
	}
	const Component = lazyContent(block.className);
	return (
		<div className={'compare-block compare-block-live' + positionClass} style={positionStyle} data-component={block.className}>
			{showsLiveBadge(block.className) && (
				<span className="compare-block-live-badge">Live data — not compared</span>
			)}
			<BlockErrorBoundary>
				<Suspense fallback={<div className="compare-block-loading">…</div>}>
					<Component config={config} {...livePaneProps(templateConfig, block.pageSize)} />
				</Suspense>
			</BlockErrorBoundary>
		</div>
	);
};

// A FIXED/RELATIVE template page drawn to spec (2A, plan-compareDocuments.md §3.12 A):
// a pt-sized sheet with the page's background, scaled uniformly to the pane width.
// transform: scale() doesn't affect layout, so the viewport wrapper reserves the
// scaled height and clips the unscaled overflow; sheetScale caps at 1 (a wide pane
// shows the sheet at natural size, like the editor).
// The viewport's size comes from CSS alone (aspect-ratio + max-width), never from
// `scale`: the ResizeObserver watches the viewport, so a height set from its own
// callback re-resizes the observed element mid-delivery and raises "ResizeObserver
// loop completed with undelivered notifications" on every resize
// (plan-compareDocuments.md §3.12 E). The callback may only touch the inner sheet.
export const CompareSheet = ({ layout, templateStyles, children }) => {
	const viewportRef = useRef(null);
	const [scale, setScale] = useState(1);

	useLayoutEffect(() => {
		const el = viewportRef.current;
		if (!el)
			return undefined;
		const update = () => setScale(sheetScale(el.clientWidth, layout.size.width));
		update();
		// jsdom has no ResizeObserver; the initial measure still runs there.
		if (typeof ResizeObserver === 'undefined')
			return undefined;
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, [layout.size.width]);

	const background = resolveSheetBackground(layout, templateStyles);
	// Flow sheets (3A) may run past the page bounds — browser metrics drift a line
	// or two from the PDF's — so they show the overflow instead of clipping it.
	const flow = !!layout.flow;
	return (
		<div
			className={'compare-sheet-viewport' + (flow ? ' compare-sheet-viewport-flow' : '')}
			ref={viewportRef}
			style={{
				aspectRatio: `${layout.size.width} / ${layout.size.height}`,
				maxWidth: layout.size.width + 'pt',
			}}
		>
			<div
				className={'compare-sheet' + (flow ? ' compare-sheet-flow' : '')}
				style={{
					width: layout.size.width + 'pt',
					height: layout.size.height + 'pt',
					transform: scale < 1 ? `scale(${scale})` : undefined,
					backgroundColor: background || undefined,
				}}
			>
				{children}
			</div>
		</div>
	);
};

const ComparePane = ({
	side, refValue, options, onRefChange, content, pages, templateConfig, templateName, tickers,
	diff, additionalAddins, showChanges, onToggleChanges, bodyRef, onBodyScroll, paneKeyGen,
	onOpenComments, commentsSelected, editorLink,
}) => {
	const remote = useRemote();
	const history = useHistory();

	const snapshotData = useMemo(() => {
		if (!templateConfig || !content || content.status !== 'ok')
			return null;
		return createSnapshotData({
			remote,
			contentMap: content.data,
			templateConfig,
			templateName,
			tickers,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [content, templateConfig, templateName]);

	// `pages` arrives from ComparePage (one collectPages per side — the pane's
	// own content map drives flow-page splitting, item 6, and ComparePage also
	// needs the counts for the rail, so it owns the computation).

	const fontConfig = useMemo(() => ({
		custom: {
			families: templateConfig ? Object.keys(templateConfig.fonts || {}) : [],
			urls: templateConfig ? Object.values(templateConfig.fonts || {}).map((font) => font.importLink) : [],
		},
	}), [templateConfig]);

	let body;
	if (!content || !templateConfig) {
		body = <div className="compare-pane-message">Loading…</div>;
	} else if (content.status !== 'ok') {
		body = <div className={`compare-pane-message compare-pane-message-${content.status}`}>{content.message}</div>;
	} else {
		body = (
			<WebfontLoader config={fontConfig}>
				<DataContext.Provider value={snapshotData}>
					<div className="compare-pane-document">
						{pages.map(({ pageIdx, templateIdx, blocks, layout }) => {
							const blockNodes = blocks.map((block) => (
								// Keyed on the diff generation (both refs + diff readiness):
								// the Raw editors read `load` once at mount, so a version
								// switch OR fresh annotations must remount them.
								<CompareBlock
									key={paneKeyGen + '|' + block.key}
									block={block}
									side={side}
									templateConfig={templateConfig}
									contentMap={content.data || {}}
									diffKeys={diff ? diff.keys : null}
									additionalAddins={additionalAddins}
								/>
							));
							return (
								// One wrapper per template page — the centre rail's page
								// navigation scroll-spies and jumps between these. Empty
								// pages keep their wrapper so numbering matches the editor.
								// FIXED/RELATIVE pages draw as a laid-out sheet (2A); a
								// layout page with no size falls back to the flat stack.
								// data-template-idx is the cross-pane page identity the
								// scroll sync anchors on (split flow segments share it).
								<div className="compare-pane-page" data-page-idx={pageIdx} data-template-idx={templateIdx} key={paneKeyGen + '|page' + pageIdx}>
									{layout?.size
										? (
											<CompareSheet layout={layout} templateStyles={templateConfig.styles}>
												{blockNodes}
											</CompareSheet>
										)
										: blockNodes}
								</div>
							);
						})}
					</div>
				</DataContext.Provider>
			</WebfontLoader>
		);
	}

	// Controls mirror around the centre rail (per the design): the left pane leads
	// with its icons ([comment][eye][select]), the right pane trails with them.
	// No onOpenComments means comments are switched off (COMPARE_COMMENTS_ENABLED
	// in ComparePage) — render no icon, like the rail's optional comments button.
	const commentLabel = `Comments for this version (${side} pane)`;
	const commentToggle = !onOpenComments ? null : (
		<Tooltip label={commentLabel} placement="top" delay={1000}>
			<button
				type="button"
				className={'compare-comment-toggle' + (commentsSelected ? ' compare-comment-toggle-active' : '')}
				aria-label={commentLabel}
				onClick={onOpenComments}
			>
				<MessageSquare size={20} />
			</button>
		</Tooltip>
	);
	const eyeLabel = showChanges ? 'Hide changes' : 'Show changes';
	const eyeToggle = (
		<Tooltip label={eyeLabel} placement="top" delay={1000}>
			<button
				type="button"
				className={'compare-eye-toggle' + (showChanges ? ' compare-eye-toggle-on' : '')}
				aria-label={eyeLabel}
				onClick={onToggleChanges}
			>
				{showChanges ? <Eye size={20} /> : <EyeOff size={20} />}
			</button>
		</Tooltip>
	);
	const versionSelect = <VersionSelect options={options} value={refValue} onChange={onRefChange} />;
	// Item 8 follow-up (2026-08-21 feedback): the editor deep link sits beside the
	// version selector — it edits the CURRENT DRAFT regardless of the pane's selected
	// version, so it belongs next to the "Current draft" menu, not in the page header.
	// ComparePage passes editorLink to the LEFT pane only.
	const editButton = !editorLink ? null : (
		<Tooltip label="Open in Editor" placement="top" delay={1000}>
			<button
				type="button"
				className="compare-editor-link"
				aria-label="Open in Editor"
				onClick={() => history.push(editorLink)}
			>
				<Edit size={20} />
			</button>
		</Tooltip>
	);

	return (
		<div className={`compare-pane compare-pane-${side}` + (showChanges ? '' : ' hide-changes')}>
			<div className="compare-pane-header">
				{side === 'left'
					? <>{commentToggle}{eyeToggle}{editButton}{versionSelect}</>
					: <>{versionSelect}{eyeToggle}{commentToggle}</>}
			</div>
			<div className="compare-pane-body" ref={bodyRef} onScroll={onBodyScroll}>
				{body}
			</div>
		</div>
	);
};

export default ComparePane;
